import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";

const SITE_NAME = "VerhuisMatch";
const DEFAULT_OG_IMAGE = "https://verhuismatch.com/og-image.jpg";
const BASE_URL = "https://verhuismatch.com";

interface SEOHeadProps {
  title: string;
  description: string;
  canonical?: string;
  ogImage?: string;
  noIndex?: boolean;
  schemaJson?: Record<string, unknown> | Record<string, unknown>[];
  ogType?: string;
  /** Extra children to render inside Helmet (e.g. hreflang links, article meta) */
  children?: React.ReactNode;
}

const SEOHead = ({
  title,
  description,
  canonical,
  ogImage = DEFAULT_OG_IMAGE,
  noIndex = false,
  schemaJson,
  ogType = "website",
  children,
}: SEOHeadProps) => {
  const location = useLocation();

  // Auto-generate canonical from current path if not explicitly provided
  const resolvedCanonical = canonical
    ? canonical.startsWith("http")
      ? canonical
      : `${BASE_URL}${canonical}`
    : `${BASE_URL}${location.pathname}`;

  // Enforce meta description max 155 chars
  const trimmedDescription = description.length > 155
    ? description.substring(0, 152) + "..."
    : description;

  const schemas = Array.isArray(schemaJson) ? schemaJson : schemaJson ? [schemaJson] : [];

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="description" content={trimmedDescription} />
      <link rel="canonical" href={resolvedCanonical} />
      <link rel="alternate" hrefLang="nl-NL" href={resolvedCanonical} />
      <link rel="alternate" hrefLang="nl" href={resolvedCanonical} />
      <link rel="alternate" hrefLang="x-default" href={resolvedCanonical} />
      {noIndex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph */}
      <meta property="og:title" content={title} />
      <meta property="og:description" content={trimmedDescription} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={resolvedCanonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="nl_NL" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={trimmedDescription} />
      <meta name="twitter:image" content={ogImage} />

      {/* Schema.org JSON-LD */}
      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}

      {children}
    </Helmet>
  );
};

export default SEOHead;
